import {
  ConnectionStatus,
  ParticipantType,
  NotificationsType,
  NotificationEntities,
  prisma,
  Prisma,
} from "client";
import { createNotification, updateNotification } from "../notification";
import { sendExpoNotification } from "@sajilni/utils/expoNotification";
import { getDeviceTokensByUser } from "services/deviceToken";
import { expandConnectionParticipantSides } from "../userHub";
import {
  participantGroupColorSelect,
  pickCounterpartyGroupColor,
} from "../participantGroup/party-group-color.pure";

/** Company ids are globally addressable, so always scope receiver validation to the event. */
export const connectionCompanyExistsOnEvent = async ({
  entityId,
  eventId,
  type,
}: {
  entityId: string;
  eventId: string;
  type: "EXHIBITOR" | "SPONSOR";
}) => {
  const where = { id: entityId, eventId };
  const company =
    type === ParticipantType.EXHIBITOR
      ? await prisma.exhibitor.findFirst({ where, select: { id: true } })
      : await prisma.sponsor.findFirst({ where, select: { id: true } });
  return Boolean(company);
};

export const createConnectionRequest = async ({
  senderId,
  senderType,
  receiverId,
  receiverType,
  eventId,
  lang = "en",
  directly,
}: {
  senderId: string;
  senderType: Prisma.ConnectionRequestCreateInput["senderType"];
  receiverId: string;
  receiverType: Prisma.ConnectionRequestCreateInput["receiverType"];
  eventId: string;
  lang?: string;
  directly?: boolean;
}) => {
  try {
    const data: Prisma.ConnectionRequestCreateInput = {
      senderType,
      receiverType,
      status: directly ? ConnectionStatus.ACCEPTED : ConnectionStatus.PENDING,
      event: { connect: { id: eventId } },
    };

    // Exhibitors and sponsors have dedicated entities. Every other participant
    // type (speaker, visitor, media, support, user, other) is backed by User.
    if (senderType === ParticipantType.EXHIBITOR) {
      data.senderExhibitor = { connect: { id: senderId } };
    } else if (senderType === ParticipantType.SPONSOR) {
      data.senderSponsor = { connect: { id: senderId } };
    } else {
      data.senderUser = { connect: { id: senderId } };
    }

    if (receiverType === ParticipantType.EXHIBITOR) {
      data.receiverExhibitor = { connect: { id: receiverId } };
    } else if (receiverType === ParticipantType.SPONSOR) {
      data.receiverSponsor = { connect: { id: receiverId } };
    } else {
      data.receiverUser = { connect: { id: receiverId } };
    }
    const isThereConnectionRequest = await prisma.connectionRequest.findFirst({
      where: {
        AND: [
          {
            OR: [
              { senderId, receiverId },
              { senderId: receiverId, receiverId: senderId },
            ],
          },
          { status: { not: ConnectionStatus.REJECTED } },
          { eventId },
        ],
      },
    });
    if (isThereConnectionRequest) {
      throw new Error("Connection request already exists");
    }
    const connectionRequest = await prisma.connectionRequest.create({
      data,
      include: {
        senderExhibitor: {
          // ponytail: first translation beats empty {{receiver.name}} when Accept-Language misses stored lang
          include: { translations: { take: 1 } },
        },
        senderSponsor: {
          include: { translations: { take: 1 } },
        },
        senderUser: {
          include: {
            profiles: {
              where: { eventId, active: true },
              take: 1,
            },
          },
        },
        receiverExhibitor: {
          include: { translations: { take: 1 } },
        },
        receiverSponsor: {
          include: { translations: { take: 1 } },
        },
        receiverUser: {
          include: {
            profiles: {
              where: { eventId, active: true },
              take: 1,
            },
          },
        },
      },
    });

    await createNotification({
      type: directly
        ? NotificationsType.APPROVED_CONNECTION_REQUEST
        : NotificationsType.NEW_CONNECTION_REQUEST,
      entity: NotificationEntities.CONNECTION_REQUEST,
      entityId: connectionRequest.id,
      senderId: connectionRequest.senderId,
      receiverId: connectionRequest.receiverId,
      sender: data.senderType,
      receiver: data.receiverType,
      eventId: connectionRequest.eventId,
    });
    const expoPushTokens = await getDeviceTokensByUser(receiverId, eventId);
    const senderName =
      connectionRequest?.senderUser?.profiles?.[0]?.name ||
      connectionRequest?.senderUser?.name ||
      connectionRequest?.senderSponsor?.translations?.[0]?.name ||
      connectionRequest.senderExhibitor?.translations?.[0]?.name;
    await sendExpoNotification(
      receiverId,
      expoPushTokens,
      directly
        ? `You have accepted the connection request from ${senderName}`
        : `Have Connect Request From ${senderName}`,
      {
        type: "connect_request",
      }
    );

    return connectionRequest;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/** Latest non-rejected connection status between two entity ids (user/exhibitor/sponsor) for one event. */
export const getConnectionStatusBetween = async (
  entityIdA: string,
  entityIdB: string,
  eventId: string
) => {
  const connection = await prisma.connectionRequest.findFirst({
    where: {
      eventId,
      OR: [
        { senderId: entityIdA, receiverId: entityIdB },
        { senderId: entityIdB, receiverId: entityIdA },
      ],
      status: { not: ConnectionStatus.REJECTED },
    },
    select: { status: true },
    orderBy: { createdAt: "desc" },
  });
  return connection?.status ?? null;
};

const nameContains = (search: string) => ({
  contains: search,
  mode: "insensitive" as const,
});

/** Pure where builder for list ownership + optional counterparty search. */
export function buildConnectionRequestsWhere({
  entityIds,
  eventId,
  status,
  search,
}: {
  entityIds: string[];
  eventId: string;
  status?: ConnectionStatus;
  search?: string;
}): Prisma.ConnectionRequestWhereInput {
  const ownership: Prisma.ConnectionRequestWhereInput = {
    OR: [{ senderId: { in: entityIds } }, { receiverId: { in: entityIds } }],
  };

  // Match the other party only — sent + received both show under my-connections.
  // Include Profile.name (same display source as list/notification UI).
  const searchFilter: Prisma.ConnectionRequestWhereInput | null = search
    ? {
        OR: [
          {
            AND: [
              { senderId: { in: entityIds } },
              {
                OR: [
                  { receiverUser: { name: nameContains(search) } },
                  {
                    receiverUser: {
                      profiles: {
                        some: {
                          eventId,
                          name: nameContains(search),
                        },
                      },
                    },
                  },
                  {
                    receiverExhibitor: {
                      translations: { some: { name: nameContains(search) } },
                    },
                  },
                  {
                    receiverSponsor: {
                      translations: { some: { name: nameContains(search) } },
                    },
                  },
                ],
              },
            ],
          },
          {
            AND: [
              { receiverId: { in: entityIds } },
              {
                OR: [
                  { senderUser: { name: nameContains(search) } },
                  {
                    senderUser: {
                      profiles: {
                        some: {
                          eventId,
                          name: nameContains(search),
                        },
                      },
                    },
                  },
                  {
                    senderExhibitor: {
                      translations: { some: { name: nameContains(search) } },
                    },
                  },
                  {
                    senderSponsor: {
                      translations: { some: { name: nameContains(search) } },
                    },
                  },
                ],
              },
            ],
          },
        ],
      }
    : null;

  return {
    eventId,
    ...(status ? { status } : {}),
    AND: [ownership, ...(searchFilter ? [searchFilter] : [])],
  };
}

export const getConnectionRequests = async ({
  entityIds,
  status,
  page = 1,
  limit = 10,
  eventId,
  search,
}: {
  entityIds: string[];
  status?: ConnectionStatus;
  page?: number;
  limit?: number;
  eventId: string;
  search?: string;
}) => {
  const where = buildConnectionRequestsWhere({
    entityIds,
    eventId,
    status,
    search: search?.trim() || undefined,
  });

  const skip = Math.max(0, (page - 1) * limit);

  const [items, total] = await Promise.all([
    prisma.connectionRequest.findMany({
      where,
      skip,
      take: limit,
      orderBy: { createdAt: "desc" },
      include: {
        senderUser: {
          include: {
            profiles: {
              where: { eventId },
              orderBy: { createdAt: "desc" },
              take: 1,
              include: {
                participantGroups: participantGroupColorSelect,
              },
            },
          },
        },
        senderExhibitor: {
          include: {
            translations: true,
            participantGroup: participantGroupColorSelect,
          },
        },
        senderSponsor: {
          include: {
            translations: true,
            participantGroup: participantGroupColorSelect,
          },
        },
        receiverUser: {
          include: {
            profiles: {
              where: { eventId },
              orderBy: { createdAt: "desc" },
              take: 1,
              include: {
                participantGroups: participantGroupColorSelect,
              },
            },
          },
        },
        receiverExhibitor: {
          include: {
            translations: true,
            participantGroup: participantGroupColorSelect,
          },
        },
        receiverSponsor: {
          include: {
            translations: true,
            participantGroup: participantGroupColorSelect,
          },
        },
        notifications: true,
      },
    }),
    prisma.connectionRequest.count({ where }),
  ]);

  return {
    items: items.map((item) => ({
      ...item,
      // Counterparty group color from relations already loaded above.
      color: pickCounterpartyGroupColor(item, entityIds),
    })),
    total,
    page,
    totalPages: Math.max(1, Math.ceil(total / limit)),
    allowedEntityIds: entityIds,
  };
};

async function canActAsConnectionEntity(
  actorUserId: string,
  entityId: string,
  entityType: ParticipantType,
  eventId: string
): Promise<boolean> {
  if (entityId === actorUserId) return true;

  if (
    entityType !== ParticipantType.EXHIBITOR &&
    entityType !== ParticipantType.SPONSOR
  ) {
    return false;
  }

  const user = await prisma.user.findUnique({
    where: { id: actorUserId },
    select: { email: true },
  });

  // teamIds first
  const byTeam =
    entityType === ParticipantType.EXHIBITOR
      ? await prisma.exhibitor.findFirst({
          where: { id: entityId, eventId, teamIds: { has: actorUserId } },
          select: { id: true },
        })
      : await prisma.sponsor.findFirst({
          where: { id: entityId, eventId, teamIds: { has: actorUserId } },
          select: { id: true },
        });
  if (byTeam) return true;

  // Mongo email equality is case-sensitive.
  if (!user?.email) return false;
  const company =
    entityType === ParticipantType.EXHIBITOR
      ? await prisma.exhibitor.findFirst({
          where: { id: entityId, eventId },
          select: { id: true, email: true },
        })
      : await prisma.sponsor.findFirst({
          where: { id: entityId, eventId },
          select: { id: true, email: true },
        });
  return (
    !!company?.email && company.email.toLowerCase() === user.email.toLowerCase()
  );
}

async function assertCanRespondToConnection(
  actorUserId: string,
  connection: {
    senderId: string;
    senderType: ParticipantType;
    receiverId: string;
    receiverType: ParticipantType;
    eventId: string;
  }
) {
  if (
    !(await canActAsConnectionEntity(
      actorUserId,
      connection.receiverId,
      connection.receiverType,
      connection.eventId
    ))
  ) {
    throw new Error("Forbidden");
  }
  // Block only personally originated requests (multi-company actors may
  // canActAs sender company and receiver company on different entities).
  if (connection.senderId === actorUserId) {
    throw new Error("Forbidden");
  }
}

export const updateConnectionRequestStatus = async (
  id: string,
  status: ConnectionStatus,
  notificationId: string | undefined,
  lang: string = "en",
  actorUserId: string,
  eventId?: string
) => {
  try {
    const connection = await prisma.connectionRequest.findUnique({
      where: { id },
      include: { notifications: true },
    });
    if (!connection) {
      throw new Error("Connection request not found");
    }
    if (eventId && connection.eventId !== eventId) {
      throw new Error("Forbidden");
    }
    if (connection.status !== ConnectionStatus.PENDING) {
      throw new Error("Only pending connection requests can be updated.");
    }

    await assertCanRespondToConnection(actorUserId, connection);

    const connectionRequest = await prisma.connectionRequest.update({
      where: { id },
      data: { status },
      include: {
        notifications: true,
        senderExhibitor: {
          include: { translations: { take: 1 } },
        },
        senderSponsor: {
          include: { translations: { take: 1 } },
        },
        senderUser: {
          include: {
            profiles: {
              where: { eventId: connection.eventId, active: true },
              take: 1,
            },
          },
        },
        receiverExhibitor: {
          include: { translations: { take: 1 } },
        },
        receiverSponsor: {
          include: { translations: { take: 1 } },
        },
        receiverUser: {
          include: {
            profiles: {
              where: { eventId: connection.eventId, active: true },
              take: 1,
            },
          },
        },
      },
    });

    const ownedNotificationIds = new Set(
      connection.notifications.map((n) => n.id)
    );
    // Ignore client notificationId unless it belongs to this connection.
    const resolvedNotificationId =
      (notificationId && ownedNotificationIds.has(notificationId)
        ? notificationId
        : undefined) ||
      connection.notifications.find(
        (n) => n.type === NotificationsType.NEW_CONNECTION_REQUEST
      )?.id ||
      connection.notifications[0]?.id;

    if (resolvedNotificationId) {
      await updateNotification(resolvedNotificationId, {
        type:
          status === ConnectionStatus.ACCEPTED
            ? NotificationsType.APPROVED_CONNECTION_REQUEST
            : status === ConnectionStatus.REJECTED
            ? NotificationsType.REJECTED_CONNECTION_REQUEST
            : NotificationsType.NEW_CONNECTION_REQUEST,
        readAt: new Date(),
        // Refresh relative time in the bell — content changed to accept/reject.
        sentAt: new Date(),
      });
    }

    await createNotification({
      type:
        status === ConnectionStatus.ACCEPTED
          ? NotificationsType.APPROVED_CONNECTION_REQUEST
          : status === ConnectionStatus.REJECTED
          ? NotificationsType.REJECTED_CONNECTION_REQUEST
          : NotificationsType.NEW_CONNECTION_REQUEST,
      entity: NotificationEntities.CONNECTION_REQUEST,
      entityId: connectionRequest.id,
      senderId: connectionRequest.receiverId,
      receiverId: connectionRequest.senderId,
      sender: connectionRequest.receiverType,
      receiver: connectionRequest.senderType,
      eventId: connectionRequest.eventId,
    });
    const expoPushTokens = await getDeviceTokensByUser(
      connectionRequest.senderId,
      connectionRequest.eventId
    );
    const senderName =
      connectionRequest?.receiverUser?.profiles?.[0]?.name ||
      connectionRequest?.receiverUser?.name ||
      connectionRequest?.receiverSponsor?.translations?.[0]?.name ||
      connectionRequest.receiverExhibitor?.translations?.[0]?.name;

    await sendExpoNotification(
      connectionRequest.senderId,
      expoPushTokens,
      `Your Connect Request with ${senderName} has been ${connectionRequest.status.toLocaleLowerCase()}.`,
      {
        type: "connect_request",
      }
    );
    return connectionRequest;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
export const deleteConnectionRequest = async (id: string) => {
  return await prisma.connectionRequest.delete({
    where: { id },
  });
};
export const checkConnectionRequestBetweenParticipant = async (
  senderId: string | string[],
  receiverId: string | string[],
  eventId?: string
) => {
  try {
    let senderIds = (Array.isArray(senderId) ? senderId : [senderId]).filter(
      Boolean
    );
    let receiverIds = (
      Array.isArray(receiverId) ? receiverId : [receiverId]
    ).filter(Boolean);
    if (!senderIds.length || !receiverIds.length) return null;

    if (eventId) {
      const expanded = await expandConnectionParticipantSides(
        senderIds,
        receiverIds,
        eventId
      );
      senderIds = expanded.senderIds;
      receiverIds = expanded.receiverIds;
    }

    const where: Prisma.ConnectionRequestWhereInput = {
      OR: [
        { senderId: { in: senderIds }, receiverId: { in: receiverIds } },
        { senderId: { in: receiverIds }, receiverId: { in: senderIds } },
      ],
      ...(eventId ? { eventId } : {}),
    };

    // Multiple requests can exist (re-send after reject). Accepted must win.
    const accepted = await prisma.connectionRequest.findFirst({
      where: { ...where, status: ConnectionStatus.ACCEPTED },
    });
    if (accepted) return accepted;

    // REJECTED is not an active gate. createConnectionRequest already allows
    // a new request after reject; returning REJECTED blocked company replies.
    return await prisma.connectionRequest.findFirst({
      where: { ...where, status: { not: ConnectionStatus.REJECTED } },
      orderBy: { createdAt: "desc" },
    });
  } catch (e) {
    console.error(e);
    throw e;
  }
};
export const getConnectionRequestById = async (id: string) => {
  try {
    const connectionRequest = await prisma.connectionRequest.findUnique({
      where: { id },
    });
    return connectionRequest;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
